import { Avatar, formatNum } from "./common";
import { Crown, Flame, Sparkles, Shirt, Star, Gem, Award, Coins, Check, Sparkle } from "lucide-react";

const BADGE_ICONS = { crown: Crown, flame: Flame, sparkles: Sparkles, shirt: Shirt, star: Star, gem: Gem };

// Frame + animation-aware avatar used on profiles, store cards and previews.
export function FramedAvatar({ src, name, size = 96, frame, animation, ringClass = "ring-4 ring-[#030303]" }) {
  const ring = frame?.config?.ring;
  const glow = frame?.config?.glow;
  const anim = animation?.config;
  const animClass = anim?.target === "avatar" ? `vs-anim-${anim.effect}` : "";
  if (ring) {
    return (
      <div className={`rounded-full p-[3px] inline-block ${animClass}`} style={{ background: ring, boxShadow: glow || undefined }}>
        <div className="rounded-full bg-[#030303] p-[2px]"><Avatar src={src} name={name} size={size} /></div>
      </div>
    );
  }
  return <div className={`inline-block ${animClass}`}><Avatar src={src} name={name} size={size} className={ringClass} /></div>;
}

export function BadgePill({ badge, testid }) {
  const cfg = badge.config || {};
  const Icon = BADGE_ICONS[cfg.icon] || Award;
  const color = cfg.color || "#d4af37";
  return (
    <span data-testid={testid} className="text-xs rounded-full px-2.5 py-1 flex items-center gap-1 font-medium border"
      style={{ color, borderColor: color + "66", background: color + "1f" }}>
      {badge.image_url ? <img src={badge.image_url} className="w-3.5 h-3.5 rounded-full object-cover" alt="" /> : <Icon size={12} />}
      {cfg.label || badge.name}
    </span>
  );
}

export function bgStyle(bg) {
  return bg?.config?.value ? { background: bg.config.value } : null;
}

// A square visual preview for a store item depending on its kind.
export function ItemVisual({ item, size = "h-32" }) {
  const k = item.kind;
  if (k === "background") {
    return <div className={`w-full ${size} rounded-xl border border-white/10`} style={bgStyle(item) || {}} />;
  }
  if (k === "badge") {
    return <div className={`w-full ${size} rounded-xl border border-white/10 flex items-center justify-center surface-2`}><BadgePill badge={item} /></div>;
  }
  // frame / animation — show a sample avatar
  const frame = k === "frame" ? item : null;
  const animation = k === "animation" ? item : null;
  return (
    <div className={`w-full ${size} rounded-xl border border-white/10 flex items-center justify-center surface-2 relative overflow-hidden`}>
      {k === "animation" && item.config?.target === "name"
        ? <span className="vs-anim-shimmer text-gold font-display font-bold text-lg">VirtuStar</span>
        : <FramedAvatar src={null} name="VS" size={56} frame={frame} animation={animation} />}
    </div>
  );
}

export function PriceTag({ price }) {
  if (!price || price <= 0) return <span className="text-xs font-semibold text-emerald-400">Free</span>;
  return <span className="text-sm font-bold text-gold flex items-center gap-1"><Coins size={13} />{formatNum(price)}</span>;
}

export function StoreItemCard({ item, onClick, onBuy, owned, testid }) {
  return (
    <div className="surface rounded-2xl p-3 card-hover flex flex-col" data-testid={testid}>
      <button onClick={onClick} className="block text-left">
        <ItemVisual item={item} />
      </button>
      <div className="mt-2.5 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate flex items-center gap-1">
            {item.featured && <Sparkle size={12} className="text-gold shrink-0" />}{item.name}
          </div>
          <div className="text-[11px] text-muted-foreground capitalize">{item.kind}</div>
          {item.kind === "badge" && item.vstar_boost > 0 && (
            <div className="text-[11px] font-semibold text-eblue mt-0.5" data-testid={`${testid}-boost`}>+{formatNum(item.vstar_boost)} VStar</div>
          )}
        </div>
        <PriceTag price={item.price} />
      </div>
      <div className="mt-2.5">
        {owned || item.owned ? (
          <div className="w-full h-9 rounded-full surface-2 border border-emerald-400/30 text-emerald-400 text-sm flex items-center justify-center gap-1" data-testid={`${testid}-owned`}><Check size={14} />Owned</div>
        ) : (
          <button onClick={() => onBuy(item)} data-testid={`${testid}-buy`}
            className="w-full h-9 rounded-full btn-blue text-sm font-semibold flex items-center justify-center gap-1">
            {item.price > 0 ? <>Get for <Coins size={13} />{formatNum(item.price)}</> : "Get free"}
          </button>
        )}
      </div>
    </div>
  );
}
